/**
 * AI-Hub service — COPILOT_MASTER_PLAN subsystems D / E / H / R (client portal).
 *
 * Powers the three AI-Hub pages:
 *   • /ai-approvals — HITL review center  (subsystem H + R)
 *   • /ai-activity  — AI observability     (subsystem E + D)
 *   • /ai-graph     — live plan graph      (subsystem P/MAS + D)
 *
 * Every live source below is FEATURE-DETECTED and DEGRADES GRACEFULLY. Sibling
 * agents land some of these mid-run (R-backend approvals, MAS plan graph), so
 * each family probes a small set of candidate paths once per SPA session,
 * caches the first that answers with a usable shape, and — when none is live —
 * returns an explicit `source: 'none'` so the UI can render a clean empty state
 * instead of erroring.
 *
 * NOTE: task list / cancel / realtime reuse the already-shipped
 * `copilot-tasks.service.ts` (subsystem E). This module owns the NEW families:
 * HITL/draft approvals, persistence policy, activity breakdown, and the MAS
 * plan graph.
 */

import { get, post, put } from '@/lib/api/client'
import { resolveApiUrl } from '@/lib/api/config'
import { getTenantId, getUserId, keycloak } from '@/lib/auth/keycloak'

// ── shared helpers ───────────────────────────────────────────────────────────

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

/** 404/405/501 = endpoint not (yet) deployed → try the next candidate. */
export function isMissingEndpoint(err: unknown): boolean {
  const s = httpStatus(err)
  return s === 404 || s === 405 || s === 501 || s === undefined
}

type Row = Record<string, unknown>

function asItems(payload: unknown): Row[] | null {
  if (Array.isArray(payload)) return payload as Row[]
  if (payload && typeof payload === 'object') {
    const obj = payload as Row
    for (const key of ['items', 'approvals', 'pending', 'results', 'data', 'tasks', 'plans', 'events', 'subtasks', 'nodes']) {
      if (Array.isArray(obj[key])) return obj[key] as Row[]
    }
  }
  return null
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return null
}

// ═══════════════════════════════════════════════════════════════════════════
//  HITL + DRAFT APPROVALS  (subsystem H + R)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `kind` is authoritative for the action path (from the R-backend unified feed):
 *   • 'draft' → POST {base}/approvals/{id}/approve|reject  (promotes/soft-deletes a draft record)
 *   • 'hitl'  → POST /agentic/hitl/respond                  (ai-orchestrator HITL; R does NOT proxy)
 * `source` mirrors it for badge display (kept for back-compat).
 */
export type ApprovalKind = 'draft' | 'hitl'
export type ApprovalSource = 'draft' | 'agentic-hitl' | 'unified'

export interface PendingApproval {
  id: string
  /** Authoritative action-routing discriminator. */
  kind: ApprovalKind
  /** Badge display source. */
  source: ApprovalSource
  title: string
  /** e.g. "Créer un risque", "Publier une politique". */
  proposedAction?: string
  /** Human module/domain label. */
  domain?: string
  /** Free-form summary/description shown in the list. */
  summary?: string
  /** Full proposed payload / new values (rendered in the detail modal). */
  payload?: unknown
  /** Prior values, when this is a draft-promotion / update (diff before). */
  before?: unknown
  /** Proposed values (diff after). */
  after?: unknown
  entityType?: string
  entityId?: string
  /** Linked source task id (deep-link into /ai-activity). */
  taskId?: string | null
  riskClass?: string
  createdAt?: string
  /** The exact endpoint family the approve/reject calls must use (draft rows). */
  _endpoints: {
    approve: string
    reject: string
  }
}

export type ApprovalsSourceKind = 'unified' | 'agentic-hitl' | 'none'

export interface PendingApprovalsResult {
  approvals: PendingApproval[]
  /** Which live source served this list, or 'none' when nothing is up. */
  source: ApprovalsSourceKind
  /** True when the unified R-backend approvals API answered (draft + HITL). */
  unifiedLive: boolean
  /** {draft, hitl} counts from the unified feed, when present. */
  counts?: { draft: number; hitl: number }
}

/**
 * Unified LIST endpoints tried in order (first usable shape wins). The LIVE
 * R-backend is `/ai-governance/hitl/*` (coordinator-confirmed); the others are
 * defensive fallbacks in case of a path rename.
 */
const APPROVAL_LIST_CANDIDATES: { path: string; base: string }[] = [
  { path: '/ai-governance/hitl/approvals/unified-pending', base: '/ai-governance/hitl' },
  { path: '/ai-governance/approvals/unified-pending', base: '/ai-governance/approvals' },
  { path: '/governance-pro/ai-hitl/unified-pending', base: '/governance-pro/ai-hitl' },
]

let approvalsSource: ApprovalsSourceKind | 'unknown' = 'unknown'
let approvalsBasePath = '/ai-governance/hitl' // default to the live base

export function resetApprovalsSource(): void {
  approvalsSource = 'unknown'
  approvalsBasePath = '/ai-governance/hitl'
}

function normalizeApproval(row: Row, base: string): PendingApproval {
  const rawKind = String(row.kind ?? '').toLowerCase()
  const kind: ApprovalKind = rawKind === 'hitl' ? 'hitl' : rawKind === 'draft' ? 'draft' : 'draft'
  const id = String(row.id ?? row.approval_id ?? row.request_id ?? row.hitl_id ?? '')
  const payload = row.payload ?? row.proposed ?? row.proposed_values ?? row.new_values ?? row.data ?? null
  return {
    id,
    kind,
    source: kind === 'hitl' ? 'agentic-hitl' : 'draft',
    title:
      str(row.title) ??
      str(row.entity_title) ??
      str(row.action_label) ??
      str(row.summary) ??
      'Élément en attente',
    proposedAction: str(row.proposed_action) ?? str(row.action) ?? str(row.action_type) ?? str(row.operation),
    domain: str(row.domain) ?? str(row.module) ?? str(row.entity_type),
    summary: str(row.summary) ?? str(row.description) ?? str(row.reason),
    payload,
    before: row.before ?? row.old_values ?? row.current_values ?? null,
    after: row.after ?? row.new_values ?? row.proposed_values ?? payload ?? null,
    entityType: str(row.entity_type),
    entityId: str(row.entity_id),
    taskId: str(row.task_id) ?? str(row.backend_task_id) ?? null,
    riskClass: str(row.risk_class) ?? str(row.side_effect) ?? str(row.risk),
    createdAt: str(row.created_at) ?? str(row.requested_at),
    _endpoints: {
      approve: `${base}/approvals/${id}/approve`,
      reject: `${base}/approvals/${id}/reject`,
    },
  }
}

/** Fallback list: the existing agentic HITL queue (always tried last). */
async function fetchAgenticHitl(): Promise<PendingApproval[] | null> {
  const payload = await get<unknown>('/agentic/hitl/pending')
  const items = asItems(payload)
  if (items == null) return null
  return items
    .map((row) => {
      const id = String(row.id ?? row.request_id ?? row.hitl_id ?? '')
      const p = row.payload ?? row.context ?? row.proposed ?? null
      return {
        id,
        kind: 'hitl' as ApprovalKind,
        source: 'agentic-hitl' as ApprovalSource,
        title: str(row.title) ?? str(row.action) ?? str(row.skill_id) ?? 'Validation agentique',
        proposedAction: str(row.action) ?? str(row.action_type) ?? str(row.operation),
        domain: str(row.domain) ?? str(row.module),
        summary: str(row.summary) ?? str(row.description) ?? str(row.question),
        payload: p,
        before: null,
        after: p,
        taskId: str(row.task_id) ?? null,
        riskClass: str(row.risk_class) ?? str(row.side_effect),
        createdAt: str(row.created_at) ?? str(row.requested_at),
        _endpoints: {
          // The agentic queue resolves via POST /agentic/hitl/respond (below).
          approve: '/agentic/hitl/respond',
          reject: '/agentic/hitl/respond',
        },
      } as PendingApproval
    })
    .filter((a) => a.id)
}

/**
 * Fetch the unified pending-approvals list. Never throws — absent backends
 * yield `{approvals: [], source: 'none'}` and the page shows an empty state.
 */
export async function fetchPendingApprovals(): Promise<PendingApprovalsResult> {
  // 1. Unified R-backend approvals (draft + HITL), when landed.
  if (approvalsSource === 'unified' || approvalsSource === 'unknown') {
    for (const cand of APPROVAL_LIST_CANDIDATES) {
      try {
        const payload = await get<Row>(cand.path)
        const items = asItems(payload)
        if (items != null) {
          approvalsSource = 'unified'
          approvalsBasePath = cand.base
          const counts = payload?.counts as { draft?: number; hitl?: number } | undefined
          return {
            approvals: items.map((r) => normalizeApproval(r, approvalsBasePath)),
            source: 'unified',
            unifiedLive: true,
            counts: counts ? { draft: counts.draft ?? 0, hitl: counts.hitl ?? 0 } : undefined,
          }
        }
      } catch (err) {
        if (!isMissingEndpoint(err)) {
          // Known-good endpoint transient error — surface empty but keep source.
          if (approvalsSource === 'unified') {
            return { approvals: [], source: 'unified', unifiedLive: true }
          }
        }
        // else: try the next candidate.
      }
    }
  }

  // 2. Fallback: the existing agentic HITL queue.
  if (approvalsSource !== 'none') {
    try {
      const approvals = await fetchAgenticHitl()
      if (approvals != null) {
        approvalsSource = 'agentic-hitl'
        return { approvals, source: 'agentic-hitl', unifiedLive: false }
      }
    } catch (err) {
      if (!isMissingEndpoint(err) && approvalsSource === 'agentic-hitl') {
        return { approvals: [], source: 'agentic-hitl', unifiedLive: false }
      }
    }
  }

  approvalsSource = 'none'
  return { approvals: [], source: 'none', unifiedLive: false }
}

export interface ApprovalDecisionResult {
  ok: boolean
  /** Set when the whole family is unreachable (offline / not deployed). */
  offline?: boolean
}

/**
 * ai-orchestrator HITL respond body (R does NOT proxy these). The API client
 * also injects Authorization + X-Tenant-ID, but the endpoint contract wants the
 * ids in the body, so we source them from the parsed token.
 */
function hitlRespondBody(hitlId: string, decision: 'APPROVED' | 'REJECTED', comment?: string) {
  return {
    hitl_id: hitlId,
    tenant_id: getTenantId() ?? undefined,
    user_id: getUserId() ?? undefined,
    decision,
    decision_data: comment ? { comment } : {},
  }
}

/** Approve a pending item via the exact family its `kind` dictates. */
export async function approveApproval(a: PendingApproval, note?: string): Promise<ApprovalDecisionResult> {
  if (a.kind === 'hitl') {
    try {
      await post('/agentic/hitl/respond', hitlRespondBody(a.id, 'APPROVED', note))
      return { ok: true }
    } catch (err) {
      return { ok: false, offline: isMissingEndpoint(err) }
    }
  }
  // draft → promotes the draft record to final.
  try {
    await post(a._endpoints.approve, {})
    return { ok: true }
  } catch (err) {
    return { ok: false, offline: isMissingEndpoint(err) }
  }
}

/** Reject a pending item via the exact family its `kind` dictates. */
export async function rejectApproval(a: PendingApproval, note?: string): Promise<ApprovalDecisionResult> {
  if (a.kind === 'hitl') {
    try {
      await post('/agentic/hitl/respond', hitlRespondBody(a.id, 'REJECTED', note))
      return { ok: true }
    } catch (err) {
      return { ok: false, offline: isMissingEndpoint(err) }
    }
  }
  // draft → soft-delete the draft record.
  try {
    await post(a._endpoints.reject, { soft_delete: true })
    return { ok: true }
  } catch (err) {
    return { ok: false, offline: isMissingEndpoint(err) }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  ENQUEUE A DRAFT APPROVAL  (subsystem H + R — Companion draft_review path)
// ═══════════════════════════════════════════════════════════════════════════

export interface EnqueueApprovalInput {
  /** Where the record came from (badge/audit). */
  source: string
  entity_type?: string
  entity_id?: string
  /** e.g. "Créer un risque". */
  proposed_action?: string
  /** Free-form payload rendered in the review modal (title + full values). */
  payload?: Record<string, unknown>
}

/**
 * Enqueue a HITL approval for a Companion-created draft record. Called when the
 * resolved persistence policy is `draft_review`: the record is written with a
 * draft status by the create endpoint, and THIS call registers it in the
 * /ai-approvals review queue so a human promotes (approve) or discards (reject).
 *
 * Best-effort + feature-detected: it tries the LIVE R-backend base first and the
 * defensive fallbacks, returning {ok:false} without throwing when none answer —
 * a missing enqueue must never break the (already-succeeded) create.
 */
export async function enqueueDraftApproval(
  input: EnqueueApprovalInput,
): Promise<{ ok: boolean; id?: string }> {
  const bases = ['/ai-governance/hitl', '/ai-governance/approvals', '/governance-pro/ai-hitl']
  // Resolve the base already discovered by the list call when available.
  const ordered = approvalsBasePath ? [approvalsBasePath, ...bases.filter((b) => b !== approvalsBasePath)] : bases
  const body = {
    kind: 'draft',
    source: input.source,
    entity_type: input.entity_type,
    entity_id: input.entity_id,
    proposed_action: input.proposed_action,
    payload: input.payload ?? {},
  }
  for (const base of ordered) {
    try {
      const res = await post<Row>(`${base}/approvals`, body)
      const id = str(res?.id) ?? str(res?.approval_id)
      return { ok: true, id }
    } catch (err) {
      if (!isMissingEndpoint(err)) {
        // Real error on a known-live endpoint → stop; the create already landed.
        return { ok: false }
      }
      // else: try the next base.
    }
  }
  return { ok: false }
}

// ═══════════════════════════════════════════════════════════════════════════
//  PERSISTENCE POLICY  (subsystem H — "brouillon vs définitif")
// ═══════════════════════════════════════════════════════════════════════════

export type PersistencePolicyMode = 'draft_review' | 'permanent' | 'ask'

export interface PersistencePolicy {
  mode: PersistencePolicyMode
  /** True when a live policy API answered (else this is a local default). */
  live: boolean
}

const POLICY_CANDIDATES = [
  '/ai-governance/hitl/policy', // LIVE (coordinator-confirmed)
  '/ai-governance/approvals/policy',
  '/governance-pro/ai-hitl/policy',
]

let policyPath = '' // resolved GET/PUT path once discovered

function normalizePolicyMode(v: unknown): PersistencePolicyMode {
  const s = String(v ?? '').toLowerCase()
  if (s === 'draft_review' || s === 'draft' || s === 'review') return 'draft_review'
  if (s === 'permanent' || s === 'auto' || s === 'commit') return 'permanent'
  if (s === 'ask' || s === 'prompt') return 'ask'
  return 'draft_review'
}

export async function fetchPersistencePolicy(): Promise<PersistencePolicy> {
  const candidates = policyPath ? [policyPath] : POLICY_CANDIDATES
  for (const path of candidates) {
    try {
      const payload = await get<Row>(path)
      policyPath = path
      const mode = normalizePolicyMode(
        payload?.mode ?? payload?.persistence_policy ?? payload?.policy ?? payload?.value,
      )
      return { mode, live: true }
    } catch (err) {
      if (!isMissingEndpoint(err)) {
        return { mode: 'draft_review', live: false }
      }
    }
  }
  // Nothing live → the safe default is draft_review (creations saved for review).
  return { mode: 'draft_review', live: false }
}

export async function updatePersistencePolicy(mode: PersistencePolicyMode): Promise<PersistencePolicy> {
  const candidates = policyPath ? [policyPath] : POLICY_CANDIDATES
  // scope=user so each user's choice resolves user→tenant→system.
  const body = { mode, scope: 'user' as const }
  for (const path of candidates) {
    try {
      await put(path, body)
      policyPath = path
      return { mode, live: true }
    } catch (err) {
      if (!isMissingEndpoint(err)) {
        return { mode, live: false }
      }
    }
  }
  return { mode, live: false }
}

// ═══════════════════════════════════════════════════════════════════════════
//  AI-ACTIVITY SUMMARY  (subsystem E — R-backend observability rollup)
// ═══════════════════════════════════════════════════════════════════════════

export interface AiActivitySummary {
  tasksTotal: number
  creditsTotal: number
  bySource: { key: string; count: number }[]
  byStatus: { key: string; count: number }[]
  live: boolean
}

const ACTIVITY_CANDIDATES = ['/ai-governance/hitl/ai-activity', '/ai-governance/ai-activity']

function toPairs(obj: unknown): { key: string; count: number }[] {
  if (!obj || typeof obj !== 'object') return []
  return Object.entries(obj as Record<string, unknown>).map(([key, v]) => ({ key, count: num(v) ?? 0 }))
}

/**
 * Optional server-side rollup that enriches the observability cards. Absent
 * backend → {live:false} and the page falls back to client-derived metrics.
 */
export async function fetchAiActivitySummary(): Promise<AiActivitySummary> {
  for (const path of ACTIVITY_CANDIDATES) {
    try {
      const p = await get<Row>(path)
      return {
        tasksTotal: num(p.tasks_total) ?? 0,
        creditsTotal: num(p.credits_total) ?? 0,
        bySource: toPairs(p.by_source),
        byStatus: toPairs(p.by_status),
        live: true,
      }
    } catch (err) {
      if (!isMissingEndpoint(err)) break
    }
  }
  return { tasksTotal: 0, creditsTotal: 0, bySource: [], byStatus: [], live: false }
}

// ═══════════════════════════════════════════════════════════════════════════
//  MAS PLAN GRAPH  (subsystem P — dependency DAG)
// ═══════════════════════════════════════════════════════════════════════════

export type MasNodeStatus =
  | 'PENDING'
  | 'READY'
  | 'BLOCKED'
  | 'CLAIMED'
  | 'RUNNING'
  | 'VERIFYING'
  | 'HITL'
  | 'DONE'
  | 'FAILED'
  | 'CANCELLED'

export interface MasSubtask {
  id: string
  key: string
  title: string
  status: MasNodeStatus
  progress: number | null
  priority?: number | null
  agentRole?: string
  risk?: string
  dependsOn: string[]
  error?: string | null
  result?: unknown
}

export interface MasPlan {
  id: string
  goal?: string
  status?: string
  progressPct?: number | null
  strategy?: string
  creditsUsed?: number | null
  subtasks: MasSubtask[]
  /** Locks held (resource_key → holder) — annotate edges/nodes. */
  locks?: { resourceKey: string; holder?: string; subtaskKey?: string }[]
}

function normalizeMasStatus(v: unknown): MasNodeStatus {
  const s = String(v ?? '').toUpperCase()
  const all: MasNodeStatus[] = [
    'PENDING', 'READY', 'BLOCKED', 'CLAIMED', 'RUNNING', 'VERIFYING', 'HITL', 'DONE', 'FAILED', 'CANCELLED',
  ]
  if (all.includes(s as MasNodeStatus)) return s as MasNodeStatus
  if (s === 'IN_PROGRESS' || s === 'STARTED') return 'RUNNING'
  if (s === 'COMPLETED' || s === 'SUCCESS') return 'DONE'
  if (s === 'ERROR') return 'FAILED'
  if (s === 'QUEUED') return 'PENDING'
  return 'PENDING'
}

function normalizeSubtask(row: Row): MasSubtask {
  const dep = row.depends_on ?? row.dependsOn ?? row.deps ?? []
  return {
    id: String(row.id ?? row.key ?? ''),
    key: str(row.key) ?? String(row.id ?? ''),
    title: str(row.title) ?? str(row.description) ?? str(row.key) ?? 'Sous-tâche',
    status: normalizeMasStatus(row.status),
    progress: num(row.progress) ?? num(row.progress_pct),
    priority: num(row.priority),
    agentRole: str(row.agent_role) ?? str(row.assignee) ?? str(row.role),
    risk: str(row.risk) ?? str(row.risk_class),
    dependsOn: Array.isArray(dep) ? (dep as unknown[]).map(String) : [],
    error: str(row.error_message) ?? str(row.error) ?? null,
    result: row.result ?? null,
  }
}

let masAvailable: boolean | 'unknown' = 'unknown'

export function getMasAvailability(): boolean | 'unknown' {
  return masAvailable
}

/** List MAS plans (newest first). Returns [] when MAS isn't deployed. */
export async function fetchMasPlans(): Promise<{ plans: { id: string; goal?: string; status?: string }[]; live: boolean }> {
  try {
    const payload = await get<unknown>('/agentic/mas/plans')
    const items = asItems(payload)
    if (items == null) {
      masAvailable = false
      return { plans: [], live: false }
    }
    masAvailable = true
    return {
      plans: items
        .map((r) => ({ id: String(r.id ?? ''), goal: str(r.goal), status: str(r.status) }))
        .filter((p) => p.id),
      live: true,
    }
  } catch (err) {
    masAvailable = isMissingEndpoint(err) ? false : masAvailable === 'unknown' ? false : masAvailable
    return { plans: [], live: false }
  }
}

/** Fetch a single MAS plan with its subtasks + locks. Null when unavailable. */
export async function fetchMasPlan(planId: string): Promise<MasPlan | null> {
  try {
    const payload = await get<Row>(`/agentic/mas/plans/${planId}`)
    masAvailable = true
    const subRows =
      asItems(payload.subtasks) ??
      asItems((payload.plan_json as Row | undefined)?.subtasks) ??
      asItems(payload) ??
      []
    const lockRows = asItems(payload.locks) ?? []
    return {
      id: String(payload.id ?? planId),
      goal: str(payload.goal),
      status: str(payload.status),
      progressPct: num(payload.progress_pct),
      strategy: str(payload.strategy),
      creditsUsed: num(payload.credits_used),
      subtasks: subRows.map(normalizeSubtask).filter((s) => s.key),
      locks: lockRows.map((l) => ({
        resourceKey: String(l.resource_key ?? l.resourceKey ?? ''),
        holder: str(l.holder),
        subtaskKey: str(l.subtask_key) ?? str(l.subtaskKey),
      })),
    }
  } catch (err) {
    if (isMissingEndpoint(err)) masAvailable = false
    return null
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  DEGRADED GRAPH — build a graph from a single task's steps/result
// ═══════════════════════════════════════════════════════════════════════════

/**
 * When MAS isn't up, derive a linear/step graph from a durable task's detail
 * (steps or audit_log). Returns null when the task has no derivable steps.
 */
export function graphFromTaskSteps(taskDetail: Row | null | undefined): MasPlan | null {
  if (!taskDetail) return null
  const stepsRaw =
    asItems(taskDetail.steps) ??
    asItems(taskDetail.plan) ??
    asItems((taskDetail.result as Row | undefined)?.steps) ??
    asItems(taskDetail.audit_log) ??
    null
  if (!stepsRaw || stepsRaw.length === 0) return null

  const subtasks: MasSubtask[] = stepsRaw.map((row, i) => {
    const key = str(row.key) ?? str(row.id) ?? `step-${i + 1}`
    const prevKey = i > 0 ? (str(stepsRaw[i - 1].key) ?? str(stepsRaw[i - 1].id) ?? `step-${i}`) : undefined
    return {
      id: key,
      key,
      title: str(row.title) ?? str(row.name) ?? str(row.op) ?? str(row.action) ?? `Étape ${i + 1}`,
      status: normalizeMasStatus(row.status ?? (taskDetail.status === 'DONE' ? 'DONE' : 'PENDING')),
      progress: num(row.progress),
      priority: i + 1,
      agentRole: str(row.agent_role) ?? str(row.op) ?? str(row.tool),
      risk: str(row.side_effect) ?? str(row.risk),
      dependsOn: prevKey ? [prevKey] : [],
      error: str(row.error) ?? null,
      result: row.result ?? null,
    }
  })

  return {
    id: String(taskDetail.id ?? 'task'),
    goal: str(taskDetail.goal) ?? str(taskDetail.title),
    status: str(taskDetail.status),
    progressPct: num(taskDetail.progress_pct),
    subtasks,
    locks: [],
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  TASK DETAIL  (subsystem E — for the activity detail modal + step-graph)
// ═══════════════════════════════════════════════════════════════════════════

/** Full detail for one task. Tries the tray API then the durable engine. */
export async function fetchTaskDetail(id: string): Promise<Row | null> {
  for (const path of [`/agentic/copilot-tasks/${id}`, `/agentic/tasks/${id}`]) {
    try {
      const payload = await get<Row>(path)
      if (payload && typeof payload === 'object') return payload
    } catch (err) {
      if (!isMissingEndpoint(err)) return null
    }
  }
  return null
}

// ═══════════════════════════════════════════════════════════════════════════
//  CREATE AN AGENTIC TASK  (used by the "Nouvelle tâche" action + E2E)
// ═══════════════════════════════════════════════════════════════════════════

export interface CreateTaskInput {
  goal: string
  domain?: string
  context?: Record<string, unknown>
}

export async function createAgenticTask(input: CreateTaskInput): Promise<{ id: string } | null> {
  try {
    const payload = await post<Row>('/agentic/tasks/', {
      goal: input.goal,
      domain: input.domain,
      context: input.context ?? {},
    })
    const id = String(payload?.id ?? payload?.task_id ?? '')
    return id ? { id } : null
  } catch {
    return null
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  APPROVAL REALTIME  (subsystem R — approval.approved | approval.rejected)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Subscribe to approval lifecycle events over the notification WS
 * (`approval.approved` / `approval.rejected`, source `ai-governance-hitl`, plus
 * `task.hitl_pending`). ONE connection attempt per SPA session; if the gateway
 * WS proxy isn't reachable the socket fails once and the caller's 10s poll
 * carries the page. Returns an unsubscribe function.
 *
 * This opens a socket distinct from the Companion task-tray socket so the
 * approvals page stays live regardless of whether the tray is mounted; both are
 * cheap and the gateway allows multiple per-user connections.
 */
let approvalSocket: WebSocket | null = null
const approvalListeners = new Set<() => void>()
let approvalRtState: 'unknown' | 'active' | 'unavailable' = 'unknown'

export function subscribeApprovalEvents(onEvent: () => void): () => void {
  approvalListeners.add(onEvent)

  if (approvalRtState === 'unknown' && typeof WebSocket !== 'undefined' && keycloak.token) {
    try {
      const httpUrl = resolveApiUrl('/notifications/ws')
      const wsUrl = `${httpUrl.replace(/^http/, 'ws')}?token=${encodeURIComponent(keycloak.token)}`
      const socket = new WebSocket(wsUrl)
      approvalSocket = socket
      let opened = false
      socket.onopen = () => {
        opened = true
        approvalRtState = 'active'
      }
      socket.onmessage = (evt) => {
        try {
          const data = JSON.parse(String(evt.data)) as { kind?: string; type?: string; source?: string }
          const kind = data.kind ?? data.type ?? ''
          if (kind.startsWith('approval.') || kind === 'task.hitl_pending') {
            approvalListeners.forEach((fn) => fn())
          }
        } catch {
          /* non-JSON frame — ignore */
        }
      }
      socket.onerror = () => {
        if (!opened) approvalRtState = 'unavailable'
      }
      socket.onclose = () => {
        approvalSocket = null
        approvalRtState = 'unavailable'
      }
    } catch {
      approvalRtState = 'unavailable'
    }
  }

  return () => {
    approvalListeners.delete(onEvent)
    if (approvalListeners.size === 0 && approvalSocket) {
      try {
        approvalSocket.close()
      } catch {
        /* already closed */
      }
      approvalSocket = null
      if (approvalRtState === 'active') approvalRtState = 'unknown'
    }
  }
}
